import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import AuditReport from "@/components/AuditReport";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuditDetailPage() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/audit/${id}`, { timeout: 15000 });
        if (!cancelled) setReport(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Update document title + OG meta + JSON-LD for shareability & SEO/GEO
  useEffect(() => {
    if (!report) return;
    const verdict = report.verdict || "AUDIT";
    document.title = `[${verdict}] ${report.brand} ${report.model} — Fakta Produk`;

    const setMeta = (selector, attr, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [keyName, value] = selector.replace(/[[\]]/g, "").split("=");
        el.setAttribute(keyName, value.replace(/['"]/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, content);
    };

    const desc =
      (report.reality_check || "").slice(0, 180) ||
      `Audit ${verdict} untuk ${report.brand} ${report.model} di pasar Indonesia.`;
    const title = `[${verdict}] ${report.brand} ${report.model} — Fakta Produk`;
    const ogImage = `${BACKEND_URL}/api/og/${report.id}.png`;
    const pageUrl = window.location.href;

    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", pageUrl);

    // JSON-LD: Review + Product (GEO + Google Rich Results)
    const verdictRatingMap = { PROCEED: 4.5, CAUTION: 3.0, AVOID: 2.0, INSUFFICIENT_DATA: null };
    const ratingValue = typeof report.overall_score === "number"
      ? Math.round((report.overall_score / 10)) / 2 * 2 / 2  // approx 0-5 scale (kept simple)
      : verdictRatingMap[verdict];

    const reviewLd = {
      "@context": "https://schema.org",
      "@type": "Review",
      "@id": `${pageUrl}#review`,
      "headline": title,
      "url": pageUrl,
      "inLanguage": "id-ID",
      "datePublished": report.timestamp,
      "author": { "@type": "Organization", "name": "Fakta Produk — Chief Auditor (PI)" },
      "publisher": { "@type": "Organization", "name": "Fakta Produk", "url": "https://faktaproduk.com/" },
      "reviewBody": [report.reality_check, report.deal_breaker, report.risk_assessment, report.actionable_advice].filter(Boolean).join(" \n\n "),
      "itemReviewed": {
        "@type": "Product",
        "name": `${report.brand} ${report.model}`.trim(),
        "brand": { "@type": "Brand", "name": report.brand },
        "category": report.category || undefined,
      },
    };
    if (typeof report.overall_score === "number") {
      reviewLd.reviewRating = {
        "@type": "Rating",
        "ratingValue": (report.overall_score / 20).toFixed(1), // 0-100 → 0-5
        "bestRating": "5",
        "worstRating": "0",
      };
      reviewLd.itemReviewed.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": (report.overall_score / 20).toFixed(1),
        "bestRating": "5",
        "worstRating": "0",
        "ratingCount": "1",
        "reviewCount": "1",
      };
    } else if (ratingValue) {
      reviewLd.reviewRating = {
        "@type": "Rating",
        "ratingValue": String(ratingValue),
        "bestRating": "5",
        "worstRating": "0",
      };
    }

    // Inject (replace previous if exists)
    let ldScript = document.head.querySelector('script[data-ld="audit-review"]');
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.setAttribute("type", "application/ld+json");
      ldScript.setAttribute("data-ld", "audit-review");
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify(reviewLd);

    return () => {
      // Cleanup canonical & JSON-LD when leaving page
      if (canonical && canonical.parentNode) canonical.setAttribute("href", window.location.origin + "/");
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
    };
  }, [report]);

  return (
    <div className="brutal min-h-screen bg-[#fdfdfd] text-[#0a0a0a]">
      <GoogleAnalytics measurementId="G-L8R7WSYR5P" />
      <header className="border-b-2 border-black bg-[#fdfdfd]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-20 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="detail-logo">
            <div className="w-8 h-8 bg-black text-white grid place-items-center font-mono font-bold">FP</div>
            <span className="font-display font-black uppercase tracking-tight text-lg md:text-xl">Fakta Produk</span>
          </Link>
          <Link
            to="/"
            className="border-2 border-black bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-yellow-50"
            data-testid="back-home"
          >
            ← Audit Produk Lain
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-20 py-10 md:py-16">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#666]">
          / Shared Audit Report
        </div>

        {loading && (
          <div className="mt-6 border-2 border-black bg-white p-8 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin" />
            <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-[#666]">Memuat laporan...</div>
          </div>
        )}

        {!loading && error && (
          <div className="mt-6 border-2 border-black bg-[#FF2400] text-white p-6" data-testid="detail-error">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]">[ERROR]</div>
            <div className="font-display font-bold text-2xl mt-1">Laporan tidak ditemukan</div>
            <p className="mt-2 text-sm">{error}</p>
            <Link
              to="/"
              className="inline-block mt-4 bg-white text-black px-4 py-2 border-2 border-white font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#FFD700]"
            >
              ← Kembali ke Beranda
            </Link>
          </div>
        )}

        {!loading && report && (
          <div className="mt-6" data-testid="shared-audit-report">
            <AuditReport report={report} forceGate={true} />

            <div className="mt-8 border-2 border-black bg-[#FFD700] p-6 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em]">Mau audit produk lain?</div>
              <Link
                to="/"
                className="mt-3 inline-block bg-black text-[#FFD700] px-6 py-3 font-mono font-bold uppercase tracking-[0.18em] border-2 border-black hover:bg-[#002FA7] hover:text-white"
                data-testid="cta-audit-another"
              >
                ▶ Audit Sekarang (Gratis)
              </Link>
            </div>
          </div>
        )}
      </section>
      <Toaster />
    </div>
  );
}
